const defined = (value) => (
  value !== undefined && value !== null && value !== ""
);

const isSectionOccurrence = (question) => Boolean(
  question?.section_origin?.occurrence_id,
);

const identityError = (code, details = {}) => {
  const error = new Error(
    code === "question_identity_duplicate"
      ? "This treatment release contains a duplicate question. Refresh or start over."
      : "This treatment release contains an ambiguous question identity. Refresh or start over.",
  );
  error.code = code;
  // Keep the machine-readable alias in the error object for support tooling,
  // while never placing internal release IDs in patient/operator copy.
  error.details = details;
  return error;
};

export const buildQuestionIdentityMap = (questions) => {
  const map = {};
  const stableIds = new Set();
  const aliasPriorities = new Map();
  const ambiguousAliasPriorities = new Map();
  const register = (alias, questionId, priority = 2) => {
    const key = String(alias);
    const value = String(questionId);
    const currentValue = map[key];
    if (currentValue === value) {
      if (priority > (aliasPriorities.get(key) ?? -1)) {
        aliasPriorities.set(key, priority);
      }
      return;
    }
    if (currentValue !== undefined) {
      const currentPriority = aliasPriorities.get(key) ?? 2;
      if (priority > currentPriority) {
        map[key] = value;
        aliasPriorities.set(key, priority);
        ambiguousAliasPriorities.delete(key);
        return;
      }
      if (priority < currentPriority) {
        return;
      }
      if (priority < 2) {
        delete map[key];
        aliasPriorities.delete(key);
        ambiguousAliasPriorities.set(key, priority);
        return;
      }
      throw identityError("question_identity_ambiguous", {
        alias: key,
        question_ids: [currentValue, value],
      });
    }
    const ambiguousPriority = ambiguousAliasPriorities.get(key);
    if (ambiguousPriority !== undefined) {
      if (priority <= ambiguousPriority) {
        return;
      }
      ambiguousAliasPriorities.delete(key);
    }
    map[key] = value;
    aliasPriorities.set(key, priority);
  };

  (Array.isArray(questions) ? questions : []).forEach((question, index) => {
    if (!question?.id) {
      throw new Error(`Question at position ${index + 1} has no stable ID.`);
    }
    const questionId = String(question.id);
    if (stableIds.has(questionId)) {
      throw identityError("question_identity_duplicate", {
        question_id: questionId,
      });
    }
    stableIds.add(questionId);
    // Immutable occurrence IDs are globally unique. A reusable Section field's
    // canonical source ID is not: the same field may legitimately occur in
    // several child Programs, while a parent Stage-1 occurrence can also use
    // it. Keep occurrence aliases authoritative and treat the canonical field
    // alias as scoped/weak for child Section occurrences. Two child-only
    // occurrences therefore require `program_id:source_id`; a parent common
    // occurrence, when present, remains the unscoped canonical target.
    register(question.id, questionId, 3);
    if (defined(question.release_node_id)) {
      register(question.release_node_id, questionId, 3);
    }
    if (defined(question.source_id)) {
      register(
        question.source_id,
        questionId,
        isSectionOccurrence(question) ? 1 : 2,
      );
    }
    const semanticAliasPriority = isSectionOccurrence(question) ? 1 : 2;
    [
      question.question_id,
      question.beluga_field_mapping,
      question.mapped_field,
      question.mappedField,
    ].filter(defined).forEach((identity) => (
      register(identity, questionId, semanticAliasPriority)
    ));
    if (question.program_id && question.source_id) {
      register(
        `${question.program_id}:${question.source_id}`,
        questionId,
        3,
      );
    }
    register(`q_${index + 1}`, questionId, 0);
    register(`q${index + 1}`, questionId, 0);
    if (question.order_index) {
      register(`q_${question.order_index}`, questionId, 1);
      register(`q${question.order_index}`, questionId, 1);
    }
  });
  return map;
};
